import { Img, Lightning, Router } from '@lightningjs/sdk'

import { getColor, grey300, grey50, grey800, grey920 } from '../../constants/colors'
import { UniNeueBold, UniNeueRegular } from '../../constants/fonts'
import { BORDER_RADIUS } from '../../constants/general'
import { BASE_ROUTES } from '../../constants/routes'
import { fetchData } from '../../services/flosports/floExperience'
import { createSpeechTextForCards } from '../../utils/cardUtils'
import { captureClick, getCardPosition, SEGMENT_TRACK_EVENTS } from '../../utils/segmentAnalytics'
import TvBadge from '../TvBadge/TvBadge'
import { HIDDEN, TvBadges, TvCardImgPlaceholder } from './templates'
import TvBaseCard from './TvBaseCard'

const INNER_PADDING = 16

export default class TvEntityCard extends TvBaseCard {
  private _isCardFadeInEnabled = true

  static _template() {
    return {
      ...super._template(),
      Container: {
        w: (w) => w,
        h: (h) => h,
        rect: true,
        color: getColor(grey920),
        flex: {
          direction: 'column',
        },
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          radius: BORDER_RADIUS,
        },
        ImageWrapper: {
          w: 388,
          h: 388,
          rtt: true,
          color: getColor(grey800),
          shader: {
            type: Lightning.shaders.RoundedRectangle,
            radius: [BORDER_RADIUS, BORDER_RADIUS, 0, 0],
          },
          Placeholder: {
            type: TvCardImgPlaceholder,
            imgSize: { w: 220, h: 200 },
          },
          Image: {
            w: (w) => w,
            h: (h) => h,
            texture: null,
            alpha: HIDDEN,
          },
          TopBadges: {
            w: (w) => w,
            h: (h) => h,
            flex: {
              direction: 'row',
              justifyContent: 'flex-end',
            },
            TopTrailingBadges: {
              type: TvBadges,
              flexItem: {
                marginRight: 16,
                marginTop: 16,
              },
            },
          },
        },
        Metadata: {
          w: 388 - INNER_PADDING * 2,
          h: 156,
          flex: {
            direction: 'column',
            padding: INNER_PADDING,
          },
          Details: {
            h: 36,
            flexItem: {
              marginBottom: 16,
            },
            text: {
              text: '',
              textColor: getColor(grey300),
              fontFace: UniNeueRegular,
              fontSize: 28,
              maxLines: 1,
              maxLinesSuffix: '...',
              wordWrapWidth: 0,
            },
          },
          Title: {
            h: 72,
            text: {
              text: '',
              textColor: getColor(grey50),
              fontFace: UniNeueBold,
              fontSize: 32,
              maxLines: 2,
              maxLinesSuffix: '...',
              wordWrapWidth: 0,
            },
          },
        },
      },
    }
  }

  get container() {
    return this.tag('Container')
  }

  get image() {
    return this.tag('Image')
  }

  get placeholder() {
    return this.tag('Placeholder')
  }

  get topTrailingBadges() {
    return this.tag('TopTrailingBadges') as TvBadges
  }

  get speechText(): string {
    return createSpeechTextForCards(
      this.data,
      ' Select for more details . ',
      this.cardIndex,
      this.totalCards,
    )
  }

  set isCardFadeInEnabled(isEnabled: boolean) {
    this._isCardFadeInEnabled = isEnabled
  }

  _init() {
    this.image.on('txLoaded', () => {
      this.placeholder.alpha = 0

      if (this._isCardFadeInEnabled) {
        this.image.setSmooth('alpha', 1)
      } else {
        this.image.alpha = 1
      }
    })
  }

  _active() {
    this.renderCardData()
  }

  async _handleEnter() {
    void captureClick(
      SEGMENT_TRACK_EVENTS.ListItemClicked,
      this.data?.action?.analytics,
      getCardPosition(this),
    )

    if (this.data?.action?.api_call) {
      await this.actOnApiCall()
    } else {
      this.navigateToUrl()
    }
  }

  async actOnApiCall() {
    const [badge]: [TvBadge] = this.topTrailingBadges.childList.get(0)
    badge.showSpinner = true
    const response = await fetchData(this.data.action.api_call)
    badge.showSpinner = false
    this.fireAncestors('$onApiCall', response, this.data, this.rowIndex, this.cardIndex)
  }

  navigateToUrl() {
    if (this.data?.action?.url.includes(BASE_ROUTES.FAVORITE_HUB)) {
      const searchUrl = this.data?.action?.url
      const encodedUrl = encodeURIComponent(searchUrl)
      Router.navigate(`${BASE_ROUTES.FAVORITE_HUB}/${encodedUrl}`)
    }
  }

  renderCardData() {
    this.container.patch({
      ImageWrapper: {
        Image: {
          texture: this.data?.preview_image?.url
            ? Img(this.data?.preview_image?.url)?.cover(388, 388)
            : null,
        },
        TopBadges: {
          TopTrailingBadges: {
            ...(this.data?.trailing_top_badges?.length > 0 && {
              badges: this.data.trailing_top_badges,
            }),
          },
        },
      },
      Metadata: {
        Details: {
          text: {
            text: this.data?.details?.text ?? '',
            wordWrapWidth: this.cardWidth - INNER_PADDING * 2,
          },
        },
        Title: {
          text: {
            text: this.data?.title?.text ?? '',
            wordWrapWidth: this.cardWidth - INNER_PADDING * 2,
          },
        },
      },
    })
  }

  _handleClick() {
    this._handleEnter()
  }
}
